import { useTranslations } from '@vocab/react';
import type { useToast } from 'braid-design-system';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import type { PageConfig } from 'src/libs/PageConfig';
import { updateIntercomReferenceCheck } from 'src/modules/intercom';
import type { FiltersEventCaptureData } from 'src/services/jobApplicationService/buildCaptureEventData';
import mapBucketTypeToBuckets from 'src/stores/JobApplicationStore/utils/mapBucketToBuckets';
import {
  convertToIsVerifiedId,
  convertToReferencesReceived,
} from 'src/utils/eventMappers';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import { HOTJAR_TAG_STATUS_CHANGE } from '../../constants/hotjar';
import { getDashisedBucket } from '../../constants/jobApplicationStatus';
import {
  updateApplicationsStatus,
  type StatusChangeLocation,
} from '../../graphql/hirer/updateApplicationsStatus';
import { sendStatusUpdateCaptureEvent } from '../../services/jobApplicationService/sendStatusUpdateCaptureEvent';
import type { IJobApplicationStore } from '../../stores/JobApplicationStore';
import type { IJobApplication } from '../../stores/JobApplicationStore/JobApplication';
import type {
  Bucket,
  HighFitExperimentVariant,
  SearchFilters,
} from '../../stores/JobApplicationStore/types';
import type { IStore } from '../../stores/Store';
import { trackUpdateStatusEvent } from '../../utils/events';
import { sendHotjarTag } from '../../utils/hotjar';
import type { ApplicationBucket } from '../AppPageBlock/graphql/hirer-types.generated';

import translations from './.vocab';
import useHandleError from './useHandleError';
import useHandleUndoStatusChange from './useHandleUndoStatusChange';

export const updateStatusOnServer = async (
  jobApplicationStore: IJobApplicationStore,
  jobApplication: IJobApplication,
  destinationBucket: Bucket,
  statusChangeLocation: StatusChangeLocation,
  addCustomError: PageConfig['dataDogRum']['addCustomError'],
) =>
  updateApplicationsStatus(
    jobApplicationStore.positionId?.toString(),
    destinationBucket as ApplicationBucket,
    statusChangeLocation,
    [
      {
        prospectId: jobApplication.id,
        positionInList: jobApplication.positionInList,
      },
    ],
    addCustomError,
  );

interface Props {
  jobApplicationStore: IJobApplicationStore;
  jobApplication: IJobApplication;
  destinationBucket: Bucket;
  statusChangeLocation: StatusChangeLocation;
  showToast: ReturnType<typeof useToast>;
  onUpdateSuccess: () => Promise<void>;
  showSuccessMessageToast?: boolean;
  store: IStore;
  isExpanded?: boolean;
  isSeekReferenceApplication: boolean;
  applicationSeekReferenceStatus: 'standard' | 'limited' | null;
  referenceCheckCounts?: { finishedCount: number; totalCount: number };
}

export interface UseHandleStatusChangeResponse {
  handleStatusChange: ({
    jobApplicationStore,
    jobApplication,
    store,
    destinationBucket,
    statusChangeLocation,
    showToast,
    onUpdateSuccess,
    showSuccessMessageToast,
  }: Props) => Promise<void>;
}

const useHandleStatusChange = ({
  filters,
  filtersEventCaptureData,
  addCustomError,
  highFitexperimentVariant,
}: {
  filters: SearchFilters;
  filtersEventCaptureData: FiltersEventCaptureData;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
  highFitexperimentVariant: HighFitExperimentVariant;
}): UseHandleStatusChangeResponse => {
  const { t } = useTranslations(translations);
  const { handleError } = useHandleError(addCustomError);
  const { handleUndoStatusChange } = useHandleUndoStatusChange(addCustomError);
  const { getLabelFromBucket } = useBucketLabel();
  const { language } = useAppConfig();

  const showSuccessMessage = ({
    jobApplicationStore,
    showToast,
    jobApplication,
    currentBucket,
    destinationBucket,
    undoTrackUpdateStatus,
    onUpdateSuccess,
  }: {
    jobApplicationStore: IJobApplicationStore;
    showToast: ReturnType<typeof useToast>;
    jobApplication: IJobApplication;
    currentBucket: Bucket;
    destinationBucket: Bucket;
    undoTrackUpdateStatus: () => void;
    onUpdateSuccess: () => Promise<void>;
  }) => {
    showToast({
      closeLabel: t('Close'),
      tone: 'positive',
      message: t('Moved to', {
        firstName: jobApplication.firstName,
        newStatusLabel: getLabelFromBucket(
          mapBucketTypeToBuckets(destinationBucket),
        ),
      }),
      key: 'STATUS_CHANGE_TOAST_KEY',
      action: {
        label: t('Undo'),
        onClick: () =>
          handleUndoStatusChange({
            jobApplicationStore,
            destinationBucket: currentBucket,
            showToast,
            undoTrackUpdateStatus,
            addCustomError,
            onUpdateSuccess,
          }),
      },
    });
  };

  return {
    handleStatusChange: async ({
      jobApplicationStore,
      jobApplication,
      store,
      destinationBucket,
      statusChangeLocation,
      showToast,
      onUpdateSuccess,
      showSuccessMessageToast = true,
      isExpanded,
      isSeekReferenceApplication,
      referenceCheckCounts,
      applicationSeekReferenceStatus,
    }: Props) => {
      sendHotjarTag(HOTJAR_TAG_STATUS_CHANGE);

      const currentBucket = jobApplication.bucket;

      jobApplication.setIsChangingState(true);

      const success = await updateStatusOnServer(
        jobApplicationStore,
        jobApplication,
        destinationBucket,
        statusChangeLocation,
        addCustomError,
      );

      if (!success) {
        handleError(
          showToast,
          jobApplication,
          'updateStatusForProspects returned success: false',
        );
        return;
      }

      sendStatusUpdateCaptureEvent({
        statusChangeLocation,
        store,
        jobApplications: [jobApplication],
        destinationBucket,
        filters: filtersEventCaptureData,
        addCustomError,
      });

      jobApplication.setStatus(mapBucketTypeToBuckets(destinationBucket));

      jobApplication.setReadWithCountUpdated();

      jobApplicationStore.updateListStateAfterChangeStatus(jobApplication.id);

      // FIXME: this should sit in the onUpdateSuccess logic IMO.
      // leaving it here for now.
      jobApplicationStore.topUpCandidateListWithCachedJobApplications({
        filters,
        language,
        addCustomError,
      });

      const isApplicantHighFit = Boolean(
        jobApplication.fitSummary.success && jobApplication.fitSummary.value,
      );

      const trackingProps: TrackStatusUpdateAttributes = {
        applicationStatus: getDashisedBucket(destinationBucket),
        candidateId: jobApplication.candidateId,
        bucket: getDashisedBucket(currentBucket),
        prospectId: jobApplication.id,
        isVerifiedId: convertToIsVerifiedId(jobApplication.profile),
        isSeekReferenceApplication,
        referencesReceived: convertToReferencesReceived(referenceCheckCounts),
        searchLocationId: filters.homeLocationIds || [],
        searchNationalityId: filters.nationalityRightToWorkIds || [],
        isPriorityApplication: jobApplication.isPriorityApply ? 1 : 0,
        applicationSeekReferenceStatus,
        isApplicantHighFit: isApplicantHighFit ? 1 : 0,
        ...(highFitexperimentVariant !== 'ineligible'
          ? {
              hubbleExperiments: {
                cm_high_fit_applicant_explainability: {
                  name: 'cm_high_fit_applicant_explainability',
                  participantStatus: 'enrolled',
                  variant: highFitexperimentVariant,
                },
              },
            }
          : {}),
      };

      const undoTrackUpdateStatus = () => {
        trackUpdateStatusEvent({
          ...trackingProps,
        });
      };

      if (isExpanded !== undefined) {
        trackUpdateStatusEvent({
          ...trackingProps,
          candidateCardStatus: isExpanded ? 'Expand' : 'Collapse',
        });
      } else {
        trackUpdateStatusEvent({
          ...trackingProps,
          candidateCardStatus: 'No RR',
        });
      }
      if (showSuccessMessageToast) {
        showSuccessMessage({
          jobApplicationStore,
          showToast,
          jobApplication,
          currentBucket,
          destinationBucket,
          undoTrackUpdateStatus,
          onUpdateSuccess,
        });
      }

      if (destinationBucket === 'SHORTLIST') {
        updateIntercomReferenceCheck();
      }

      jobApplication.setIsChangingState(false);

      // refetchFacets is the only usecase so far.
      // I don't think this should be awaited.
      // event listeners should be doing whatever they need to do without blocking
      // this hook.
      onUpdateSuccess();
    },
  };
};

export default useHandleStatusChange;
